import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { env } from '@/lib/env';
import { storage } from '@/lib/storage';

/**
 * Fasto Ads single axios instance. Dual-mode auth against the shared Django API:
 *   • Advertiser (vendor): POST /vendor/login  → refresh via POST /vendor/refresh
 *   • Team (staff/admin):  POST /auth/api/token/ → refresh via /auth/verify-otp/refresh
 *
 * Access JWT lives in memory; the refresh JWT + auth mode are persisted so the
 * response interceptor can transparently refresh once on a 401 and replay.
 */

let accessToken: string | null = null;
let onAuthFailure: (() => void) | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}
export function getAccessToken(): string | null {
  return accessToken;
}
export function setAuthFailureHandler(fn: (() => void) | null) {
  onAuthFailure = fn;
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: env.apiBase,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken && !config.headers.has?.('Authorization')) {
    config.headers.set('Authorization', `Bearer ${accessToken}`);
  }
  return config;
});

let refreshPromise: Promise<string> | null = null;

async function performRefresh(): Promise<string> {
  const refresh = storage.getRefresh();
  if (!refresh) throw new Error('no_refresh_token');

  const mode = storage.getMode();
  const url =
    mode === 'team' ? `${env.apiBase}/auth/verify-otp/refresh` : `${env.apiBase}/vendor/refresh`;

  // Bare axios — must not go through apiClient to avoid recursion.
  const res = await axios.post(url, { refresh }, { headers: { 'Content-Type': 'application/json' } });

  const newAccess = res.data?.access as string | undefined;
  const newRefresh = res.data?.refresh as string | undefined;
  if (!newAccess) throw new Error('refresh_failed');

  setAccessToken(newAccess);
  if (newRefresh) storage.setRefresh(newRefresh); // SimpleJWT rotates
  return newAccess;
}

apiClient.interceptors.response.use(
  (r) => r,
  async (error: AxiosError<BackendError>) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const status = error.response?.status;
    const body = error.response?.data;

    const isAuthError =
      status === 401 &&
      (body?.type === 'invalid_token' ||
        body?.code === 'token_not_valid' ||
        body?.detail === 'Authentication credentials were not provided.' ||
        !!accessToken);

    if (isAuthError && original && !original._retry) {
      original._retry = true;
      try {
        refreshPromise =
          refreshPromise ??
          performRefresh().finally(() => {
            refreshPromise = null;
          });
        const fresh = await refreshPromise;
        original.headers.set('Authorization', `Bearer ${fresh}`);
        return apiClient.request(original);
      } catch {
        setAccessToken(null);
        storage.clearAuth();
        onAuthFailure?.();
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);

export interface BackendError {
  message?: string;
  type?: string;
  detail?: string;
  code?: string;
  [k: string]: unknown;
}

export function extractErrorMessage(err: unknown, fallback = 'Something went wrong.'): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as BackendError | undefined;
    if (data && typeof data === 'object') {
      if (typeof data.detail === 'string') return data.detail;
      if (typeof data.message === 'string') return data.message;
      // DRF field errors: {field: ["msg"]}
      const first = Object.values(data)[0];
      if (Array.isArray(first) && typeof first[0] === 'string') return first[0];
    }
    return err.message || fallback;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const r = await apiClient.get<T>(url, config);
  return r.data;
}

export async function post<T, B = unknown>(url: string, body?: B, config?: AxiosRequestConfig): Promise<T> {
  const r = await apiClient.post<T>(url, body, config);
  return r.data;
}

export async function patch<T, B = unknown>(url: string, body?: B, config?: AxiosRequestConfig): Promise<T> {
  const r = await apiClient.patch<T>(url, body, config);
  return r.data;
}

export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const r = await apiClient.delete<T>(url, config);
  return r.data;
}
